import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { UserService } from '../api';
import { TiveSessionService } from './tive-session.service';
import { refreshSession } from './session-refresh';

/** Marks a retried request to prevent infinite retry loops. */
const IS_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Catches HTTP 419 responses (revoked token) and transparently refreshes
 * the Auth0 token + Tive session before retrying the request.
 *
 * Must be the **first** interceptor in the chain so that retries flow
 * through the full pipeline (custom headers → Auth0 Bearer → HTTP).
 *
 * ## Why 419 happens
 *
 * After ability mutations the backend revokes affected user tokens.
 * The POST itself returns 200, but every subsequent request is rejected
 * with 419 until the client obtains a fresh token (issued *after* the
 * revocation timestamp).
 */
export const revokedPermissionInterceptorFn: HttpInterceptorFn = (req, next) => {
  // loginByEmail always gets x-login-request from the proxy — never rejected
  // with 419. Already-retried requests must not loop.
  if (req.context.get(IS_RETRY) || req.url.includes('/api/User/loginByEmail')) {
    return next(req);
  }

  const auth = inject(AuthService);
  const userService = inject(UserService);
  const session = inject(TiveSessionService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // The backend writes plain text (e.g. "RevokedPermissionChange") as
      // the 419 body.  Angular may expose that text as `error.error`,
      // as a JSON-parse failure object, or even as `null` depending on
      // the responseType the caller requested.  To be safe we treat
      // ANY 419 as a revoked-token signal.
      if (error.status !== 419) {
        return throwError(() => error);
      }

      console.warn(
        '[revoked-permission] 419 intercepted for',
        req.method,
        req.urlWithParams,
        '— error.error type:',
        typeof error.error,
        'value:',
        error.error,
      );

      return from(refreshSession(auth, userService, session)).pipe(
        switchMap(() => {
          console.warn('[revoked-permission] Retrying', req.method, req.urlWithParams);
          // Mark as retry so this interceptor skips it on the second pass
          const retryReq = req.clone({ setHeaders: {} });
          retryReq.context.set(IS_RETRY, true);
          return next(retryReq);
        }),
        // If refresh itself fails (e.g. expired refresh token), surface the original 419
        catchError((retryError) => {
          console.error('[revoked-permission] Recovery failed:', retryError);
          return throwError(() => error);
        }),
      );
    }),
  );
};
